import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getSpacesByBlogPost } from '../data/advertisingSpaces';
import blogPosts from '../data/blogPosts';
import './BlogPost.css';
import SocialShare from '../components/SocialShare';
import { Helmet } from 'react-helmet';

export default function BlogPost() {
  const { id } = useParams();
  const [blogPost, setBlogPost] = useState(null);
  const [adSpaces, setAdSpaces] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const post = blogPosts.find(p => String(p.id) === String(id));
    setBlogPost(post);
    setLoading(false);
  }, [id]);

  useEffect(() => {
    if (blogPost) {
      const spaces = getSpacesByBlogPost(id);
      setAdSpaces(spaces);
    }
  }, [id, blogPost]);

  if (loading) {
    return (
      <div className="blog-post-page">
        <div className="blog-post-container">
          <div className="loading-message">
            <Helmet>
              <title>Loading... | GradedWash Blog</title>
            </Helmet>
            <div style={{ textAlign: 'center', padding: '2em' }}>
              <div style={{ fontSize: '2em', marginBottom: '1em' }}>📖</div>
              <h2>Loading Article...</h2>
              <p>Please wait while we fetch the latest travel insights.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!blogPost) {
    return (
      <div className="blog-post-page">
        <div className="blog-post-container">
          <div className="error-message">
            <Helmet>
              <title>Blog Post Not Found | GradedWash Blog</title>
              <meta name="description" content="The blog post you're looking for doesn't exist. Explore our other travel articles and insights." />
              <link rel="canonical" href={`https://gradedwash.com/blog/${id}`} />
            </Helmet>
            <div style={{ textAlign: 'center', padding: '2em' }}>
              <div style={{ fontSize: '3em', marginBottom: '1em' }}>🔍</div>
              <h2>Blog Post Not Found</h2>
              <p>The blog post you're looking for doesn't exist or may have been moved.</p>
              <div style={{ marginTop: '2em' }}>
                <Link to="/blog" className="btn btn--primary" style={{ marginRight: '1em' }}>
                  ← Back to Blog
                </Link>
                <Link to="/" className="btn btn--secondary">
                  Go to Homepage
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Function to insert ads into content
  const renderContentWithAds = (content) => {
    if (!content) return null;
    
    const paragraphs = content.split('</p>');
    const adSpacesForThisPost = adSpaces.filter(space => space.status === 'booked');
    
    return paragraphs.map((paragraph, index) => {
      const elements = [];
      
      // Add the paragraph
      if (paragraph.trim()) {
        elements.push(<div key={`p-${index}`} dangerouslySetInnerHTML={{ __html: paragraph + '</p>' }} />);
      }
      
      // Insert ads at specific positions
      if (index === 1 && adSpacesForThisPost.find(space => space.position === 'inline-content')) {
        elements.push(
          <div key={`ad-${index}`} className="blog-ad-space inline-ad">
            <div className="ad-placeholder">
              <h4>📢 Sponsored Content</h4>
              <p>This space is reserved for premium advertisers</p>
              <a href="/advertising" className="ad-link">Book This Space</a>
            </div>
          </div>
        );
      }
      
      if (index === 3 && adSpacesForThisPost.find(space => space.position === 'inline-content-2')) {
        elements.push(
          <div key={`ad-${index}-2`} className="blog-ad-space inline-ad">
            <div className="ad-placeholder">
              <h4>📢 Sponsored Content</h4>
              <p>This space is reserved for premium advertisers</p>
              <a href="/advertising" className="ad-link">Book This Space</a>
            </div>
          </div>
        );
      }
      
      return elements;
    });
  };

  // Generate structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": blogPost.title,
    "description": blogPost.excerpt,
    "image": blogPost.image,
    "author": {
      "@type": "Person",
      "name": blogPost.author
    },
    "publisher": {
      "@type": "Organization",
      "name": "GradedWash",
      "logo": {
        "@type": "ImageObject",
        "url": "https://gradedwash.com/logo.png"
      }
    },
    "datePublished": blogPost.date,
    "dateModified": blogPost.date,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://gradedwash.com/blog/${blogPost.id}`
    }
  };

  return (
    <div className="blog-post-page">
      <Helmet>
        <title>{blogPost.title} | GradedWash Travel Blog</title>
        <meta name="description" content={blogPost.excerpt} />
        <meta name="keywords" content={blogPost.tags ? blogPost.tags.join(', ') : ''} />
        <meta property="og:title" content={blogPost.title} />
        <meta property="og:description" content={blogPost.excerpt} />
        <meta property="og:image" content={blogPost.image} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://gradedwash.com/blog/${blogPost.id}`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={blogPost.title} />
        <meta name="twitter:description" content={blogPost.excerpt} />
        <meta name="twitter:image" content={blogPost.image} />
        <link rel="canonical" href={`https://gradedwash.com/blog/${blogPost.id}`} />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>
      
      <div className="blog-post-container">
        <div className="blog-post-header">
          <Link to="/blog" className="back-link" style={{
            fontWeight: 'bold', 
            fontSize: '1.2em', 
            color: '#0070f3', 
            textDecoration: 'underline', 
            marginBottom: '1em', 
            display: 'inline-block'
          }}>
            ← Back to Blog
          </Link>
          <h1>{blogPost.title}</h1>
          <div className="blog-post-meta">
            <span className="blog-post-date">{new Date(blogPost.date).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}</span>
            <span className="blog-post-read-time">{blogPost.readTime}</span>
            <span className="blog-post-author">By {blogPost.author}</span>
            {blogPost.photographer && <span className="blog-post-photographer">Photo by {blogPost.photographer}</span>}
          </div>
          
          {/* Tags */}
          {blogPost.tags && blogPost.tags.length > 0 && (
            <div className="blog-post-tags" style={{ marginTop: '1em' }}>
              {blogPost.tags.map(tag => (
                <span key={tag} className="blog-post-tag" style={{
                  background: '#f0f0f0',
                  padding: '0.3em 0.8em',
                  borderRadius: '20px',
                  fontSize: '0.9em',
                  marginRight: '0.5em',
                  color: '#666'
                }}>
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="blog-post-content">
          <div className="blog-post-image">
            <img 
              src={blogPost.image} 
              alt={`${blogPost.title} - travel blog article image`}
              style={{ width: '100%', height: 'auto', borderRadius: '8px' }}
              onError={(e) => {
                e.target.src = 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&h=400&fit=crop&q=80';
                e.target.alt = 'Default travel image';
              }}
            />
          </div>

          {/* Header Banner Ad Space */}
          {adSpaces.find(space => space.position === 'header-banner' && space.status === 'booked') && (
            <div className="blog-ad-space header-banner">
              <div className="ad-placeholder">
                <h4>📢 Premium Header Advertisement</h4>
                <p>This space is reserved for premium advertisers</p>
                <a href="/advertising" className="ad-link">Book This Space</a>
              </div>
            </div>
          )}

          <div className="blog-post-text">
            {renderContentWithAds(blogPost.content)}
          </div>

          {/* Social Share for Blog Article */}
          <div style={{ margin: '2em 0' }}>
            <SocialShare
              url={window.location.href}
              title={blogPost.title}
              summary={blogPost.excerpt}
              hashtag="#GradedWashBlog"
            />
          </div>

          {/* Footer Banner Ad Space */}
          {adSpaces.find(space => space.position === 'footer-banner' && space.status === 'booked') && (
            <div className="blog-ad-space footer-banner">
              <div className="ad-placeholder">
                <h4>📢 Footer Advertisement</h4>
                <p>This space is reserved for premium advertisers</p>
                <a href="/advertising" className="ad-link">Book This Space</a>
              </div>
            </div>
          )}
        </div>

        <div className="blog-post-sidebar">
          {/* Sidebar Ad Spaces */}
          {adSpaces.find(space => space.position === 'sidebar-top' && space.status === 'booked') && (
            <div className="blog-ad-space sidebar-ad">
              <div className="ad-placeholder">
                <h4>📢 Sidebar Advertisement</h4>
                <p>This space is reserved for premium advertisers</p>
                <a href="/advertising" className="ad-link">Book This Space</a>
              </div>
            </div>
          )}

          <div className="related-posts">
            <h3>Related Posts</h3>
            <ul>
              {blogPosts
                .filter(post => post.id !== blogPost.id && post.category === blogPost.category)
                .slice(0, 3)
                .map(post => (
                  <li key={post.id}>
                    <Link to={`/blog/${post.id}`}>{post.title}</Link>
                  </li>
                ))}
            </ul>
          </div>

          {adSpaces.find(space => space.position === 'sidebar-middle' && space.status === 'booked') && (
            <div className="blog-ad-space sidebar-ad">
              <div className="ad-placeholder">
                <h4>📢 Sidebar Advertisement</h4>
                <p>This space is reserved for premium advertisers</p>
                <a href="/advertising" className="ad-link">Book This Space</a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
} 